import React from 'react';
import { Modal, Form, Input, Select } from 'antd';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { workwiseServices } from '../../Workw-Api/api';
import { syncDocument } from '../../utils/api';
import { removeLocalGrid } from '../../utils/localGrids';
import './SyncGridModal.css';

const PRIVACY = { PUBLIC: 'public', PRIVATE: 'private' };

/**
 * Sync (upload) a local grid to the server.
 *
 * Syncing happens while online and creates a real document, so it collects the
 * fields offline creation could not ask for (privacy, description). On failure
 * the error shows inline and the local grid is left untouched so the user can
 * retry.
 */
export default function SyncGridModal({ open, doc, onClose, onSynced }) {
  const [form] = Form.useForm();
  const [privacy, setPrivacy] = React.useState(PRIVACY.PUBLIC);
  const [apiError, setApiError] = React.useState('');
  const [isSyncing, setIsSyncing] = React.useState(false);
  // Sync is two server steps: create the document, then upload its sheets. If the
  // upload fails we keep the id so a retry reuses that document instead of
  // creating a second, orphaned one.
  const createdIdRef = React.useRef(null);

  React.useEffect(() => {
    if (open) {
      form.setFieldsValue({ name: doc?.name || '', description: doc?.description || '' });
      setPrivacy(PRIVACY.PUBLIC);
      setApiError('');
      setIsSyncing(false);
      createdIdRef.current = null;
    }
  }, [open, doc, form]);

  const handleSync = () => {
    form.validateFields().then(async (values) => {
      const trimmed = values.name.trim();
      setIsSyncing(true);
      setApiError('');

      const describe = (e, step) => {
        const detail = e?.response?.data?.message || e?.message || 'unknown error';
        return `${step}: ${detail}`;
      };

      let spreadsheetID = createdIdRef.current;

      // Step 1 — create the document (skipped when a previous attempt already did).
      if (!spreadsheetID) {
        try {
          const formData = new FormData();
          formData.append('name', trimmed);
          formData.append('description', values.description || '');
          formData.append('privacyId', privacy === PRIVACY.PUBLIC ? 1 : 2);
          formData.append('referenceId', '00000000-0000-0000-0000-000000000000');
          formData.append('referenceType', 1);
          formData.append('directoryId', '00000000-0000-0000-0000-000000000000');
          formData.append('documentType', 5);
          formData.append('attachments[0].name', trimmed);

          const response = await workwiseServices.addDocument(formData);
          spreadsheetID = response?.data?.data?.[0]?.attachments?.[0]?.attachmentId;

          if (!spreadsheetID) {
            throw new Error('the server did not return a document id');
          }
          createdIdRef.current = spreadsheetID;
        } catch (e) {
          setApiError(describe(e, "Couldn't create the document"));
          setIsSyncing(false);
          return;
        }
      }

      // Step 2 — push the offline sheets. /api/sync creates the grid document
      // from this payload when it doesn't exist yet, so there is no separate
      // create call on the grid side.
      try {
        await syncDocument({
          spreadsheetID,
          spreadsheetData: Array.isArray(doc?.sheets) ? doc.sheets : [],
        });
      } catch (e) {
        // The document exists but is empty. The local copy is deliberately kept
        // so nothing is lost, and a retry reuses the same document.
        setApiError(describe(e, 'Document created, but uploading its sheets failed'));
        setIsSyncing(false);
        return;
      }

      removeLocalGrid(doc.id);
      onSynced?.(spreadsheetID);
      onClose?.();
      setIsSyncing(false);
    });
  };

  return (
    <Modal
      title="Sync Grid"
      visible={open}
      onOk={handleSync}
      onCancel={isSyncing ? undefined : onClose}
      afterClose={() => form.resetFields()}
      okText="Sync"
      cancelText="Cancel"
      confirmLoading={isSyncing}
      okButtonProps={{ disabled: isSyncing, className: 'export-modal-btn-download' }}
      cancelButtonProps={{ className: 'export-modal-btn-cancel' }}
      maskClosable={!isSyncing}
      className="export-document-modal"
      bodyStyle={{ paddingTop: 10 }}
      centered
    >
      <Form form={form} layout="vertical">
        {/* Sync failures come from the server, not from what the user typed, so
            they belong to the modal rather than to any one field. Hanging them
            off the name input read as "fix this input", which no edit could. */}
        {apiError && (
          <div className="sync-error-banner" role="alert">
            <ExclamationCircleFilled className="sync-error-icon" />
            <span>{apiError}</span>
          </div>
        )}

        <Form.Item
          label="Grid Name"
          name="name"
          style={{ marginBottom: 14 }}
          rules={[{ required: true, message: 'Please enter a grid name.' }]}
        >
          <Input
            placeholder="Enter grid name"
            disabled={isSyncing}
            className="export-modal-input"
            onPressEnter={handleSync}
          />
        </Form.Item>

        <Form.Item label="Privacy" style={{ marginBottom: 14 }}>
          <Select
            value={privacy}
            onChange={setPrivacy}
            disabled={isSyncing}
            options={[
              { value: PRIVACY.PUBLIC, label: 'Public' },
              { value: PRIVACY.PRIVATE, label: 'Private' },
            ]}
          />
        </Form.Item>

        <Form.Item label="Description" name="description" style={{ marginBottom: 14 }}>
          <Input.TextArea
            placeholder="Enter description"
            disabled={isSyncing}
            rows={3}
            maxLength={500}
          />
        </Form.Item>

        <div className="export-format-info">
          <div className="export-format-label">
            This will upload the grid to the server and remove it from local grids.
          </div>
        </div>
      </Form>
    </Modal>
  );
}
